import { measureLatency } from '../../../gctl-measure';
import {
  AiCreditsEstimateFields,
  AiCreditsEstimate,
} from '../../../models/components/schemas/ai-credits-estimate/AiCreditsEstimate';
import {
  Select,
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  searchQueryString,
  intoSnakeCase,
} from '../../../fetchers/_helpers';
import {
  AiCreditsBatchEstimateFields,
  AiCreditsBatchEstimate,
} from '../../../models/components/schemas/ai-credits-batch-estimate/AiCreditsBatchEstimate';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetUserAiCreditsEstimateFields = readonly AiCreditsEstimateFields[];

/** Options for GET /users/{user_id}/ai_credits/estimate */
export type GetUserAiCreditsEstimateOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Possible query parameters for /users/{user_id}/ai_credits/estimate */
  query: {
    /**
     * The number of jobs to process for the AI service request.
     */
    jobNumbers?: number;

    /**
     * The name of the AI service being processed.
     *
     * Option descriptions:
     *  * `dubbing` - The service for dubbing the video with AI.
     *  * `subtitling` - The service for adding subtitles to the video with AI.
     *
     */
    serviceName: `dubbing` | `subtitling`;

    /**
     * The number of units to process for the AI service request.
     */
    serviceUnits: number;

    /**
     * The version number of the estimate system used for the AI service request.
     */
    version?: number;
  };
};

/** Result type for GET /users/{user_id}/ai_credits/estimate */
export type GetUserAiCreditsEstimateResult<Paths extends readonly string[]> = Select<
  AiCreditsEstimate,
  Paths
>;

/**
 * Get an estimated number of AI credits for a job
 * This method returns an estimated number of AI credits required to run the specified AI service.
 */
export async function getUserAiCreditsEstimate<Paths extends GetUserAiCreditsEstimateFields>({
  baseUrl,
  select,
  where: { userId },
  query,
  ...options
}: GetUserAiCreditsEstimateOptions<Paths>): Promise<GetUserAiCreditsEstimateResult<Paths>> {
  return measureLatency('getUserAiCreditsEstimate', 'GET', async () => {
    let res = await fetch(
      `${baseUrl}/users/${userId}/ai_credits/estimate?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'GET',
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as GetUserAiCreditsEstimateResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}

export type PostUserAiCreditsEstimateFields = readonly AiCreditsBatchEstimateFields[];

/** Options for POST /users/{user_id}/ai_credits/estimate */
export type PostUserAiCreditsEstimateOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Possible query parameters for /users/{user_id}/ai_credits/estimate */
  query?: {
    /**
     * Whether to include how many credits are required to run a specific video.
     */
    detailed?: boolean;
  };

  /** Data serialized to body for POST /users/{user_id}/ai_credits/estimate */
  variables: {
    /**
     * The number of jobs to process for the AI service request.
     * @example 1
     */

    jobNumbers?: number;

    /**
     * The name of the AI service being processed.
     *
     * Option descriptions:
     *  * `dubbing` - The service for dubbing the video with AI.
     *  * `subtitling` - The service for adding subtitles to the video with AI.
     *
     * @example subtitling
     */

    serviceName: `dubbing` | `subtitling`;

    /**
     * The version number of the estimate system used for the AI service request.
     * @example 1
     */

    version?: number;

    /**
     * A collection of videos to process for the AI service request.
     */

    videos: string[];
  };
};

/** Result type for POST /users/{user_id}/ai_credits/estimate */
export type PostUserAiCreditsEstimateResult<Paths extends readonly string[]> = Select<
  AiCreditsBatchEstimate,
  Paths
>;

/**
 * Get the estimated number of AI credits to run multiple resources
 * This method returns the estimated number of AI credits required to run multiple resources.
 */
export async function postUserAiCreditsEstimate<Paths extends PostUserAiCreditsEstimateFields>({
  baseUrl,
  select,
  variables,
  where: { userId },
  query,
  ...options
}: PostUserAiCreditsEstimateOptions<Paths>): Promise<PostUserAiCreditsEstimateResult<Paths>> {
  return measureLatency('postUserAiCreditsEstimate', 'POST', async () => {
    let res = await fetch(
      `${baseUrl}/users/${userId}/ai_credits/estimate?${searchQueryString(query)}&fields=${select
        .map(intoSnakeCase)
        .join(',')}`,
      {
        ...options,
        method: 'POST',
        body: JSON.stringify(deepSnakeCase(variables)),
      },
    );

    if (!res.ok) {
      throw new NetworkError('A network error occurred', res.status, res);
    }

    if (res.status === 204) {
      return null as unknown as PostUserAiCreditsEstimateResult<Paths>;
    }

    if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
      throw new Error('Expected JSON response');
    }

    let json = await res.json();
    return deepCamelCase(json);
  });
}
